import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { STUDIO_TEMPLATE_FAMILY_LABEL_KEYS } from 'src/components/star-grid/reportStudioConstants';
import type { StudioTemplateOption } from 'src/components/star-grid/reportStudio.types';

export interface StudioTemplateFamily {
  readonly id: string;
  readonly label: string;
  /** Ordered variants, each a whole definition — not a filter preset over a shared one. */
  readonly variants: readonly StudioTemplateOption[];
}

const familyId = (template: StudioTemplateOption): string => template.family || template.id;
const familyLabel = (template: StudioTemplateOption): string => template.family_label || template.label;
export const variantLabel = (template: StudioTemplateOption): string => template.variant_label || template.label;

/** Group report definitions by template family, preserving catalog order. */
export const templateFamilies = (templates: readonly StudioTemplateOption[]): StudioTemplateFamily[] => {
  const order: string[] = [];
  const byFamily = new Map<string, StudioTemplateOption[]>();
  templates.forEach((template) => {
    const id = familyId(template);
    if (!byFamily.has(id)) {
      byFamily.set(id, []);
      order.push(id);
    }
    byFamily.get(id)?.push(template);
  });
  return order.map((id) => {
    const variants = [...(byFamily.get(id) ?? [])].sort(
      (a, b) => (a.variant_order ?? 0) - (b.variant_order ?? 0) || variantLabel(a).localeCompare(variantLabel(b))
    );
    return { id, label: familyLabel(variants[0]), variants };
  });
};

/** The family containing a definition id, or undefined when the catalog has no such definition. */
export const familyOf = (families: readonly StudioTemplateFamily[], templateId: string): StudioTemplateFamily | undefined =>
  families.find((family) => family.variants.some((v) => v.id === templateId));

/**
 * The variant to open when a family is picked: the first READY one.
 *
 * A family's first variant can be unavailable (an unflattened rows hierarchy), and opening on it
 * would show the picker's own default as an error.
 */
export const defaultVariant = (family: StudioTemplateFamily): StudioTemplateOption | undefined =>
  family.variants.find((variant) => variant.ready) ?? family.variants[0];

export const studioTemplateFamilyLabel = (family: StudioTemplateFamily, text: StarGridText): string => {
  const key = STUDIO_TEMPLATE_FAMILY_LABEL_KEYS[family.id];
  return key ? text(key) : family.label;
};
